import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Nav, OverlayTrigger, Popover } from 'react-bootstrap';
import { Button, Col, Container, ListGroup, Modal, Row } from 'react-bootstrap';
import { NavLink, useNavigate } from 'react-router-dom';
import '../../styles/Mmp.css';
import { InfoCircle } from 'react-bootstrap-icons';
import { config } from '../../config';
import { DEFAULT_LOCATION, resolveLocationFromUrl } from '../../location';

interface ConditionTests {
  [key: string]: string[];
}

interface MessageMap {
  [key: string]: string;
}

const conditionTestsMap: ConditionTests = {
  AF: [
    'Cr + E (unless done in last 6 months)',
    'LFT (unless done in last 6 months)',
    'FBC (unless done in last 6 months)',
    'Weight',
    'Smoking Status',
    'Alcohol Intake',
  ],
  Asthma: ['Smoking Status', 'Alcohol Intake'],
  CKD3: [
    'Cr + E (unless done in last 6 months)',
    'ACR (unless done in last 6 months)',
    'Weight',
    'Blood Pressure (many methods)',
    'Smoking Status',
    'Alcohol Intake',
  ],
  CKD45: [],
  COPD: ['Weight', 'Oxygen Saturation', 'Spirometry (FEV1)', 'Smoking Status', 'Alcohol Intake'],
  'Type 2 Diabetes': [
    'Cr + E (unless done in last 6 months)',
    'LFT (unless done in last 6 months)',
    'FBC (unless done in last 6 months)',
    'HBA1c (Test always done)',
    'ACR (unless done in last 6 months)',
    'Weight',
    'Blood Pressure (many methods)',
    'DM Foot Check',
    'Smoking Status',
    'Alcohol Intake',
  ],
  Gout: [
    'Cr + E (unless done in last 6 months)',
    'Urate (unless done in last 6 months)',
    'Weight',
    'Blood Pressure (many methods)',
    'Smoking Status',
    'Alcohol Intake',
  ],
  Hypertension: [
    'Cr + E (unless done in last 6 months)',
    'Weight',
    'Smoking Status',
    'Alcohol Intake',
    'Full Lipid Profile',
    'Blood Pressure (many methods)',
  ],
  Hypothyroid: ['TSH Monitoring', 'Smoking Status', 'Alcohol Intake'],
  Prediabetes: ['HBA1c (Test always done)', 'Weight', 'Smoking Status', 'Alcohol Intake'],
  Vascular: [
    'Cr + E (unless done in last 6 months)',
    'LFT (unless done in last 6 months)',
    'Weight',
    'Blood Pressure (many methods)',
    'Smoking Status',
    'Alcohol Intake',
    'Full Lipid Profile',
  ],
};

const messages: MessageMap = {
  greeting: `Hello, this is Penicuik Medical Practice. We see from our records you are due your annual check-up in regards your medications (if applicable) and known medical conditions.`,
  appointment: `Would you please come to the practice for your annual blood tests with our nurse on {day} the {date} of {month} @ {selectedTime}. If possible, could you please check your blood pressure with your own home monitoring machine and check your weight prior to the appointment.`,
  appointmentCopd: `Would you please come to the practice on {day} the {date} of {month} @ {selectedTime} for your annual check with our Nurse`,
  hypertension: `Please follow the link and complete the home blood pressure chart or sign up for Inhealthcare. If you do not have access to a computer, please call the practice and ask for a Blood Pressure Pack. https://www.penicuikmedicalpractice.co.uk/self-blood-pressure-monitoring`,
  asthma: `Please follow the link and complete the questionnaire there: https://www.penicuikhealthcentre.co.uk/asthma-2`,
  copd: `Please check your weight prior to the appointment. Please also follow the link and complete the questionnaire there: https://www.penicuikhealthcentre.co.uk/copd-2`,
  end: `PLEASE DO NOT REPLY TO THIS TEXT.`,
};

function interpolate(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');
}

function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export default function Conditions() {
  const { Link } = Nav;
  const navigate = useNavigate();
  const featuresByLocation = config.featuresByLocation as Record<string, unknown> | undefined;
  const locationByHost = config.locationByHost as Record<string, string> | undefined;
  const { locationId, isKnownLocation, isPathScoped } = resolveLocationFromUrl({
    pathname: window.location.pathname,
    hostname: window.location.hostname,
    featuresByLocation,
    locationByHost,
    defaultLocation: DEFAULT_LOCATION,
  });
  const locationPrefix = isKnownLocation && isPathScoped ? `/${locationId}` : '';
  const scopedMmpHouseboundPath = `${locationPrefix}/mmp_housebound`;
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [isMmpComplete, setIsMmpComplete] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const ignoreNextPopStateRef = useRef(false);
  const leaveMessage = 'MMP is not marked complete. Leave this page anyway?';

  useEffect(() => {
    if (isMmpComplete || showLeaveModal) {
      return;
    }

    const openLeaveModal = (navigateAction: () => void) => {
      setPendingNavigation(() => navigateAction);
      setShowLeaveModal(true);
    };

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a');

      if (!anchor) {
        return;
      }

      if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) {
        return;
      }

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) {
        return;
      }

      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentPath) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openLeaveModal(() => navigate(nextPath));
    };

    const handlePopState = () => {
      if (ignoreNextPopStateRef.current) {
        ignoreNextPopStateRef.current = false;
        return;
      }

      window.history.go(1);
      openLeaveModal(() => {
        ignoreNextPopStateRef.current = true;
        window.history.back();
      });
    };

    document.addEventListener('click', handleDocumentClick, true);
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isMmpComplete, navigate, showLeaveModal]);

  const handleLeaveConfirm = () => {
    const navigateAction = pendingNavigation;
    setShowLeaveModal(false);
    setPendingNavigation(null);
    if (navigateAction) {
      navigateAction();
    }
  };

  const handleLeaveCancel = () => {
    setShowLeaveModal(false);
    setPendingNavigation(null);
  };

  const handleConditionChange = (condition: string, checked: boolean) => {
    if (checked) {
      setSelectedConditions([...selectedConditions, condition]);
    } else {
      setSelectedConditions(selectedConditions.filter((c) => c !== condition));
    }
  };

  const requiredTests = useMemo(() => {
    const testsSet = new Set<string>();
    selectedConditions.forEach((condition) => {
      const tests = conditionTestsMap[condition] || [];
      tests.forEach((test) => testsSet.add(test));
    });
    return Array.from(testsSet).sort();
  }, [selectedConditions]);

  return (
    <Container className="mmp-page-container">
      <Row>
        <Col md={{ span: 4, offset: 2 }}>
          <h4 style={{ marginBottom: '16px', color: 'var(--text-primary)', textAlign: 'center' }}>
            Select Conditions
          </h4>
          <Form>
            <div className="mmp-checkbox-group">
              <Form.Check
                type="checkbox"
                label="AF"
                onChange={(e) => handleConditionChange('AF', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Asthma"
                onChange={(e) => handleConditionChange('Asthma', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="CKD3"
                onChange={(e) => handleConditionChange('CKD3', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="CKD4/5"
                onChange={(e) => handleConditionChange('CKD45', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="COPD (Emphysema)"
                onChange={(e) => handleConditionChange('COPD', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Type 2 Diabetes"
                onChange={(e) => handleConditionChange('Type 2 Diabetes', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Gout"
                onChange={(e) => handleConditionChange('Gout', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hypertension"
                onChange={(e) => handleConditionChange('Hypertension', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hypothyroid"
                onChange={(e) => handleConditionChange('Hypothyroid', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Prediabetes (Impaired Glucose Tolerance) + Gestational Diabetes"
                onChange={(e) => handleConditionChange('Prediabetes', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Vascular Disease (Stroke, TIA, Angina/IHD/CHD, PVD/PAD, CCF)"
                onChange={(e) => handleConditionChange('Vascular', e.target.checked)}
              />
            </div>
          </Form>
        </Col>
        <Col md={{ span: 4, offset: 1 }}>
          <h4 style={{ marginBottom: '16px', color: 'var(--text-primary)', textAlign: 'center' }}>
            Required Tests
          </h4>
          <ListGroup className="required-tests-bullet-list">
            {requiredTests.map((test) => (
              <ListGroup.Item key={test} className="required-tests-bullet-item">
                <div>
                  <strong>{test}</strong>
                  {test === 'Full Lipid Profile'
                    ? selectedConditions.includes('Vascular')
                      ? ' - If never done since diagnosis'
                      : ' - If never ever done'
                    : ''}
                  {test === 'Cr + E (unless done in last 6 months)' &&
                  (selectedConditions.includes('Hypertension') ||
                    selectedConditions.includes('Vascular')) &&
                  !selectedConditions.includes('Gout') &&
                  !selectedConditions.includes('Type 2 Diabetes') &&
                  !selectedConditions.includes('CKD3') &&
                  !selectedConditions.includes('AF') ? (
                    <>
                      {' - If patient is on '}
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Popover id="info-popover" style={{ minWidth: 450 }}>
                            <Popover.Body style={{ minWidth: 450 }}>
                              <Row>
                                <Col>
                                  <ul style={{ paddingLeft: '16px', marginBottom: 0 }}>
                                    <li>Captopril</li>
                                    <li>
                                      <strong>Enalapril</strong>
                                    </li>
                                    <li>Enalapril with Hydrochlorothiazide</li>
                                    <li>Fosinopril</li>
                                    <li>Imidapril</li>
                                    <li>
                                      <strong>Lisinopril</strong>
                                    </li>
                                    <li>Lisinopril with hydrochlorothiazide</li>
                                    <li>Perindopril</li>
                                    <li>Perindopril with indapamide</li>
                                    <li>Quinapril</li>
                                    <li>Quinapril with hydrochlorothiazide</li>
                                    <li>
                                      <strong>Ramipril</strong>
                                    </li>
                                    <li>Ramipril with felodipine</li>
                                    <li>Trandolapril</li>
                                  </ul>
                                </Col>
                                <Col>
                                  <ul style={{ paddingLeft: '16px', marginBottom: 0 }}>
                                    <li>Azilsartan</li>
                                    <li>
                                      <strong>Candesartan</strong>
                                    </li>
                                    <li>Eprosartan</li>
                                    <li>
                                      <strong>Irbesartan</strong>
                                    </li>
                                    <li>Irbesartan with hydrochlorothiazide</li>
                                    <li>
                                      <strong>Losartan</strong>
                                    </li>
                                    <li>Losartan with hydrochlorothiazide</li>
                                    <li>Olmesartan</li>
                                    <li>Olmesartan with amlodipine</li>
                                    <li>Olmesartan with amlodipine and hydrochlorothiazide</li>
                                    <li>Telmisartan</li>
                                    <li>Telmisartan with hydrochlorothiazide</li>
                                    <li>
                                      <strong>Valsartan</strong>
                                    </li>
                                    <li>Valsartan with hydrochlorothiazide</li>
                                    <li>Valsartan with Sacubitril.</li>
                                  </ul>
                                </Col>
                              </Row>
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <button
                          type="button"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: 'blue',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          ACEI/ARB{' '}
                          <sup>
                            <InfoCircle style={{ marginRight: 4 }} />
                          </sup>
                        </button>
                      </OverlayTrigger>
                      or{' '}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Popover id="info-popover">
                            <Popover.Body>
                              <ul style={{ paddingLeft: '16px', marginBottom: 0 }}>
                                <li>Amiloride</li>
                                <li>Amiloride with bumetanide</li>
                                <li>
                                  <strong>Bumetanide</strong>
                                </li>
                                <li>Bendroflumethiazide</li>
                                <li>Co-amilofruse</li>
                                <li>Co-flumactone</li>
                                <li>
                                  <strong>Eplerenone</strong>
                                </li>
                                <li>
                                  <strong>Furosemide</strong>
                                </li>
                                <li>Furosemide with triamterene</li>
                                <li>
                                  <strong>Indapamide</strong>
                                </li>
                                <li>
                                  <strong>Spironolactone</strong>
                                </li>
                                <li>Spironolactone with Furosemide</li>
                                <li>Torasemide</li>
                              </ul>
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <button
                          type="button"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: 'blue',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          diuretic{' '}
                          <sup>
                            <InfoCircle style={{ marginRight: 4 }} />
                          </sup>
                        </button>
                      </OverlayTrigger>
                    </>
                  ) : (
                    ''
                  )}
                  {test === 'Blood Pressure (many methods)' &&
                  (selectedConditions.includes('Hypertension') ||
                    selectedConditions.includes('Vascular')) &&
                  !selectedConditions.includes('Gout') &&
                  !selectedConditions.includes('Type 2 Diabetes') &&
                  !selectedConditions.includes('CKD3')
                    ? " - Check on Connect Me (type 'Active Monitoring' in the earch box)"
                    : ''}
                  {test === 'LFT (unless done in last 6 months)' &&
                  selectedConditions.includes('Vascular') &&
                  !selectedConditions.includes('AF') &&
                  !selectedConditions.includes('Type 2 Diabetes') ? (
                    <>
                      {' - If patient is on '}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Popover id="info-popover">
                            <Popover.Body>
                              <ul style={{ paddingLeft: '16px', marginBottom: 0 }}>
                                <li>
                                  <strong>Apixaban</strong>
                                </li>
                                <li>Edoxoban</li>
                                <li>Fondaparinux Sodium</li>
                                <li>
                                  <strong>Rivaroxaban</strong>
                                </li>
                                <li>
                                  <strong>Warfarin</strong>
                                </li>
                              </ul>
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <button
                          type="button"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: 'blue',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          blood thinner{' '}
                          <sup>
                            <InfoCircle style={{ marginRight: 4 }} />
                          </sup>
                        </button>
                      </OverlayTrigger>
                    </>
                  ) : (
                    ''
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Col>
      </Row>
      <Row>
        <Col md={{ span: 8, offset: 2 }}>
          {(selectedConditions.includes('AF') ||
            selectedConditions.includes('Asthma') ||
            selectedConditions.includes('CKD45') ||
            selectedConditions.includes('Hypertension') ||
            selectedConditions.includes('Hypothyroid') ||
            selectedConditions.includes('Type 2 Diabetes') ||
            selectedConditions.includes('COPD')) && (
            <>
              <hr />
              <h4
                style={{ marginBottom: '16px', color: 'var(--text-primary)', textAlign: 'center' }}
              >
                Notes
              </h4>
            </>
          )}
          {selectedConditions.includes('AF') && (
            <div className="mmp-note">
              <strong>AF</strong>
              <ul>
                <li>
                  If weight &lt; 60kg and age &gt; 80, pass name to nursing team to check dose of
                  anticoagulant meds.
                </li>
                <li>20 minute appointment.</li>
              </ul>
            </div>
          )}
          {selectedConditions.includes('Asthma') && (
            <div className="mmp-note">
              <strong>Asthma</strong>
              <ul>
                <li>Link to questionnaire.</li>
                <li>20 minute appointment.</li>
              </ul>
            </div>
          )}
          {selectedConditions.includes('CKD45') && (
            <div className="mmp-note">
              <strong>CKD4 + CKD5</strong>
              <ul>
                <li>to PN to review and consider if needs passed onto Usual GP.</li>
                <li>Check if under review at hospital, if yes, code dealt with by hospital.</li>
                <li>If not dealt with by hospital goes to Jacqui or Usual GP</li>
                <li>20 minute appointment.</li>
              </ul>
            </div>
          )}
          {selectedConditions.includes('COPD') && (
            <div className="mmp-note">
              <strong>COPD</strong>
              <ul>
                <li>Text link to questionnaire, requires appointment.</li>
                <li>30 minute appointment.</li>
              </ul>
            </div>
          )}
          {selectedConditions.includes('Hypertension') && (
            <div className="mmp-note">
              <strong>Hypertension</strong>
              <ul>
                <li>Text BP link or send pack</li>
                <li>No meds = No appointment needed</li>
                <li>On Connect me = No link needed</li>
                <li>20 minute appointment.</li>
              </ul>
            </div>
          )}
          {selectedConditions.includes('Hypothyroid') && (
            <div className="mmp-note">
              <strong>Hypothyroid</strong>
              <ul>
                <li>Every 2 years.</li>
                <li>20 minute appointment.</li>
              </ul>
            </div>
          )}

          {selectedConditions.includes('Type 2 Diabetes') && (
            <div className="mmp-note">
              <strong>Type 2 Diabetes</strong>
              <ul>
                <li>20 minute appointment.</li>
              </ul>
            </div>
          )}
        </Col>
      </Row>
      <Row style={{ alignItems: 'center', marginBottom: 16 }}>
        <Col md={{ span: 8, offset: 2 }}>
          <Row>
            <hr />
            <Col>
              <Form.Label style={{ marginBottom: 0, marginRight: 8 }}>
                <strong>Date and Time of Appointment</strong>
              </Form.Label>
            </Col>
            <Col>
              <Form.Control
                type="date"
                style={{ display: 'inline-block', width: 140 }}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </Col>
            <Col>
              <Form.Control
                type="time"
                style={{ display: 'inline-block', width: 100 }}
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col md={{ span: 8, offset: 2 }}>
          <strong>If no mobile number is available a letter must be sent.</strong>
        </Col>
      </Row>
      <Row>
        <Col md={{ span: 8, offset: 2 }}>
          {((selectedDate && selectedTime) ||
            selectedConditions.includes('Hypertension') ||
            selectedConditions.includes('Asthma') ||
            (selectedConditions.includes('COPD') && selectedDate && selectedTime)) &&
            messages['greeting']}

          {selectedDate &&
            selectedTime &&
            !selectedConditions.includes('COPD') &&
            (() => {
              const dateObj = new Date(selectedDate);
              const dayNum = dateObj.getDate();
              const [hours, minutes] = selectedTime.split(':');
              const hour = parseInt(hours, 10);
              const period = hour >= 12 ? 'pm' : 'am';
              const displayHour = hour % 12 || 12;
              const formattedTime = `${displayHour}:${minutes}${period}`;
              return (
                <Row>
                  {interpolate(messages['appointment'], {
                    day: dateObj.toLocaleDateString('en-US', { weekday: 'long' }),
                    date: `${dayNum}${getOrdinalSuffix(dayNum)}`,
                    month: dateObj.toLocaleDateString('en-US', { month: 'long' }),
                    selectedTime: formattedTime,
                  })}
                </Row>
              );
            })()}

          {(selectedConditions.includes('Hypertension') ||
            selectedConditions.includes('Vascular')) &&
            (() => {
              const dateObj = new Date(selectedDate);
              const dayNum = dateObj.getDate();
              const [hours, minutes] = selectedTime.split(':');
              const hour = parseInt(hours, 10);
              const period = hour >= 12 ? 'pm' : 'am';
              const displayHour = hour % 12 || 12;
              const formattedTime = `${displayHour}:${minutes}${period}`;
              return (
                <Row>
                  {interpolate(messages['hypertension'], {
                    day: dateObj.toLocaleDateString('en-US', { weekday: 'long' }),
                    date: `${dayNum}${getOrdinalSuffix(dayNum)}`,
                    month: dateObj.toLocaleDateString('en-US', { month: 'long' }),
                    selectedTime: formattedTime,
                  })}
                </Row>
              );
            })()}

          {selectedConditions.includes('Asthma') &&
            (() => {
              const dateObj = new Date(selectedDate);
              const dayNum = dateObj.getDate();
              const [hours, minutes] = selectedTime.split(':');
              const hour = parseInt(hours, 10);
              const period = hour >= 12 ? 'pm' : 'am';
              const displayHour = hour % 12 || 12;
              const formattedTime = `${displayHour}:${minutes}${period}`;
              return (
                <Row>
                  {interpolate(messages['asthma'], {
                    day: dateObj.toLocaleDateString('en-US', { weekday: 'long' }),
                    date: `${dayNum}${getOrdinalSuffix(dayNum)}`,
                    month: dateObj.toLocaleDateString('en-US', { month: 'long' }),
                    selectedTime: formattedTime,
                  })}
                </Row>
              );
            })()}
          {selectedConditions.includes('COPD') &&
            selectedDate &&
            selectedTime &&
            (() => {
              const dateObj = new Date(selectedDate);
              const dayNum = dateObj.getDate();
              const [hours, minutes] = selectedTime.split(':');
              const hour = parseInt(hours, 10);
              const period = hour >= 12 ? 'pm' : 'am';
              const displayHour = hour % 12 || 12;
              const formattedTime = `${displayHour}:${minutes}${period}`;
              return (
                <>
                  <Row>
                    {interpolate(messages['appointmentCopd'], {
                      day: dateObj.toLocaleDateString('en-US', { weekday: 'long' }),
                      date: `${dayNum}${getOrdinalSuffix(dayNum)}`,
                      month: dateObj.toLocaleDateString('en-US', { month: 'long' }),
                      selectedTime: formattedTime,
                    })}
                  </Row>
                  <Row>{messages['copd']}</Row>
                </>
              );
            })()}

          {((selectedDate && selectedTime) ||
            selectedConditions.includes('Hypertension') ||
            selectedConditions.includes('Asthma') ||
            (selectedConditions.includes('COPD') && selectedDate && selectedTime)) &&
            messages['end']}
        </Col>
      </Row>

      <Row className="mmp-action-row">
        <Col md={{ span: 8, offset: 2 }}>
          <hr className="mmp-action-separator" />
          <Form.Check
            className="mmp-complete-checkbox"
            type="checkbox"
            id="mmp-complete"
            label={
              <span style={{ color: isMmpComplete ? '#198754' : '#dc3545', fontWeight: 700 }}>
                MMP Complete - I confirm I have added the correct Coding to Vision
              </span>
            }
            checked={isMmpComplete}
            onChange={(e) => setIsMmpComplete(e.target.checked)}
            style={{ marginBottom: 10 }}
          />
          {isMmpComplete && (
            <Button variant={'success'}>
              <Link as={NavLink} to={scopedMmpHouseboundPath}>
                Start a New MMP Assessment
              </Link>
            </Button>
          )}
        </Col>
      </Row>

      <Modal show={showLeaveModal} onHide={handleLeaveCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Leave This Page?</Modal.Title>
        </Modal.Header>
        <Modal.Body>{leaveMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleLeaveCancel}>
            Stay on Page
          </Button>
          <Button variant="danger" onClick={handleLeaveConfirm}>
            Leave Page
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
export { Conditions };
